import { useState } from "react";
import { Play } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { VIDEO_CATS } from "@/lib/i18n";
import { SectionHead } from "@/components/site/SectionHead";

const VideoCard = ({ v, t }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <article className="bg-white border border-parchment-300 rounded-2xl overflow-hidden" data-testid={`video-card-${v.id}`}>
      <div className="relative aspect-video bg-forest-900">
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}?autoplay=1&rel=0`}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`video-player-${v.id}`}
          />
        ) : (
          <button onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" aria-label={`Play ${v.title}`} data-testid={`video-play-${v.id}`}>
            <img src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="w-16 h-16 rounded-full bg-brass-500 text-forest-900 grid place-items-center shadow-xl group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 ml-1" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-5 pt-4 pb-5">
        <span className="eyebrow" data-testid={`video-category-${v.id}`}>{t(`vcat.${v.category || "other"}`)}</span>
        <h3 className="mt-1.5 text-xl font-semibold text-forest-800 leading-snug">{v.title}</h3>
      </div>
    </article>
  );
};

export default function Videos({ videos }) {
  const { t } = useLang();
  const [cat, setCat] = useState("all");

  if (!videos?.length) return null;
  const present = VIDEO_CATS.filter((c) => videos.some((v) => (v.category || "other") === c));
  const shown = cat === "all" ? videos : videos.filter((v) => (v.category || "other") === cat);
  return (
    <section id="videos" className="max-w-6xl mx-auto px-5 py-20 sm:py-28" data-testid="videos-section">
      <SectionHead eyebrow={t("vid.eyebrow")} title={t("vid.title")} text={t("vid.text")} />
      {present.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="video-filter">
          {["all", ...present].map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`video-filter-${c}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${cat === c ? "bg-forest-800 text-parchment border-forest-800" : "bg-white text-forest-800 border-parchment-300 hover:border-brass-500"}`}>
              {t(`vcat.${c}`)}
            </button>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((v) => <VideoCard key={v.id} v={v} t={t} />)}
      </div>
    </section>
  );
}
